import type { JSX } from 'react';
import { useDeferredValue, useMemo } from 'react';

import { Plus, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import type { AlertType } from '../../types/alertsConfig';

interface AlertsRulesListProps {
  alerts: AlertType[];
  selectedAlertIndex: number | null;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  onSelectAlert: (index: number) => void;
  onAddAlert: () => void;
}

const getRuleTitle = (alert: AlertType, index: number): string => {
  const id = alert.id.trim();
  const description = alert.description.trim();

  if (id) {
    return id;
  }

  if (description) {
    return `Regla ${index + 1}`;
  }

  return 'Nueva regla (sin guardar)';
};

const getRuleDescription = (alert: AlertType): string => {
  if (alert.description.trim()) {
    return alert.description.trim();
  }

  return 'Configura el ID y la descripción en el panel derecho';
};

const ruleMatchesSearch = (alert: AlertType, index: number, normalizedSearch: string): boolean => {
  if (!normalizedSearch) {
    return true;
  }

  const searchableText = [
    getRuleTitle(alert, index),
    alert.description,
    ...alert.examples,
    ...(alert.actions ?? []).map((action) => action.function),
  ]
    .join(' ')
    .toLowerCase();

  return searchableText.includes(normalizedSearch);
};

const AlertsRulesList = ({
  alerts,
  selectedAlertIndex,
  searchTerm,
  onSearchTermChange,
  onSelectAlert,
  onAddAlert,
}: AlertsRulesListProps): JSX.Element => {
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const standardCount = alerts.filter((alert) => alert.standard && !alert.disabled).length;
  const customCount = alerts.filter((alert) => !alert.standard).length;
  const normalizedSearch = deferredSearchTerm.trim().toLowerCase();
  const searchInputId = 'alerts-rules-search';

  const filteredRules = useMemo(
    () =>
      alerts
        .map((alert, index) => ({ alert, index }))
        .filter(({ alert, index }) => ruleMatchesSearch(alert, index, normalizedSearch)),
    [alerts, normalizedSearch]
  );

  return (
    <aside
      className="flex min-h-[20rem] flex-col border-b border-border-subtle bg-bg-surface-raised
        lg:min-h-[560px] lg:border-b-0 lg:border-r"
      aria-label="Lista de reglas de detección"
    >
      <div className="border-b border-border-subtle px-4 py-3">
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="m-0 text-sm font-semibold text-text-default font-display">
            {standardCount > 0
              ? `${standardCount} estándar · ${customCount} personalizada${customCount !== 1 ? 's' : ''}`
              : `${customCount} regla${customCount !== 1 ? 's' : ''} configurada${customCount !== 1 ? 's' : ''}`}
          </p>
          <Button
            type="button"
            size="sm"
            className="gap-1.5 bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark"
            onClick={onAddAlert}
          >
            <Plus className="size-3.5 shrink-0" aria-hidden />
            Nueva
          </Button>
        </div>

        <label className="relative block" htmlFor={searchInputId}>
          <span className="sr-only">Buscar regla</span>
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-text-subtle"
            aria-hidden
          />
          <Input
            id={searchInputId}
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
            placeholder="Buscar regla"
            className="h-8 bg-bg-surface pl-8 pr-2 text-xs"
            aria-label="Buscar regla"
          />
        </label>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-2">
        {filteredRules.length === 0 ? (
          <p className="m-0 rounded-lg border border-dashed border-border-subtle px-3 py-4 text-sm text-text-muted font-body">
            No hay reglas que coincidan con la búsqueda.
          </p>
        ) : (
          <ul className="m-0 list-none space-y-1 p-0">
            {filteredRules.map(({ alert, index }) => {
              const isActive = selectedAlertIndex === index;
              const title = getRuleTitle(alert, index);
              const description = getRuleDescription(alert);
              const hasActions = (alert.actions ?? []).some((action) => action.function.trim() !== '');

              return (
                <li key={`alert-rule-${index}`}>
                  <button
                    type="button"
                    className={cn(
                      'w-full rounded-md border px-3 py-2.5 text-left transition-colors',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus',
                      isActive
                        ? 'border-brand-vivid bg-brand-vivid/10 border-l-[3px]'
                        : 'border-border-subtle bg-bg-surface hover:bg-bg-surface-alt'
                    )}
                    onClick={() => onSelectAlert(index)}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span
                        className={cn(
                          'min-w-0 truncate text-sm font-semibold font-body',
                          title === 'Nueva regla (sin guardar)' ? 'italic text-brand-vivid' : 'text-text-default'
                        )}
                      >
                        {title}
                      </span>
                      {alert.standard ? (
                        <span className="shrink-0 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary">
                          Estándar
                        </span>
                      ) : null}
                      <span
                        className={cn(
                          'shrink-0 text-[10px] font-bold uppercase tracking-wider',
                          hasActions && !alert.disabled ? 'text-success' : 'text-text-subtle'
                        )}
                      >
                        {alert.disabled ? 'Desactivada' : hasActions ? 'Acción' : 'Sin acción'}
                      </span>
                    </span>
                    <span className="mt-1 block truncate text-xs text-text-muted font-body">{description}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
};

export default AlertsRulesList;
